import React from "react";
import { getPanels, accordionBlockHasValue } from "./util";
import { withBlockExtensions } from "@plone/volto/helpers";
import { useLocation, useHistory } from "react-router-dom";
import cx from "classnames";
import { RenderBlocks } from "@plone/volto/components";
import AnimateHeight from "react-animate-height";
import config from "@plone/volto/registry";
import "./editor.less";
import AccordionFilter from "./AccordionFilter";
import "./accordion.scss";

const useQuery = (location) => {
  const { search } = location;
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const View = (props) => {
  const { data, className } = props;
  const location = useLocation();
  const history = useHistory();
  const panels = getPanels(data.data);
  const metadata = props.metadata || props.properties;
  const diffView =
    location?.pathname.slice(
      location?.pathname.lastIndexOf("/"),
      location?.pathname.length,
    ) === "/diff";

  const [activeIndex, setActiveIndex] = React.useState([]);
  const [activePanel, setActivePanel] = React.useState([]);
  const [filterValue, setFilterValue] = React.useState("");
  const [itemToScroll, setItemToScroll] = React.useState("");
  const accordionConfig = config.blocks.blocksConfig.accordion;
  const iconOnRight = data.right_arrows;

  const query = useQuery(location);
  const activePanels = query.get("activeAccordion")?.split(",");
  const [firstIdFromPanels] = panels[0];

  const activePanelsRef = React.useRef(activePanels);
  const firstIdFromPanelsRef = React.useRef(firstIdFromPanels);

  const addQueryParam = (key, value) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(key, value);
    history.push({
      hash: location.hash,
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const handleClick = (id, index) => {
    const newIndex =
      activeIndex.indexOf(index) === -1
        ? data.non_exclusive
          ? [...activeIndex, index]
          : [index]
        : activeIndex.filter((item) => item !== index);

    const newPanel =
      activePanel.indexOf(id) === -1
        ? data.non_exclusive
          ? [...activePanel, id]
          : [id]
        : activePanel.filter((item) => item !== id);

    setActiveIndex(newIndex);
    setActivePanel(newPanel);
    addQueryParam("activeAccordion", newPanel);
  };

  const handleFilteredValueChange = (value) => {
    setFilterValue(value);
  };

  const scrollToElement = () => {
    if (!!activePanels && !!activePanels[0].length) {
      let element = document.getElementById(
        activePanels[activePanels.length - 1],
      );
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const isActive = (id) => activePanel.includes(id);

  React.useEffect(() => {
    !!activePanelsRef.current &&
      setItemToScroll(
        activePanelsRef.current[activePanelsRef.current?.length - 1],
      );
  }, []);

  React.useEffect(() => {
    if (data.collapsed) {
      setActivePanel(activePanelsRef.current || []);
    } else {
      if (!!activePanelsRef.current && !!activePanelsRef.current[0].length) {
        setActivePanel(activePanelsRef.current || []);
      } else {
        setActivePanel([
          firstIdFromPanelsRef.current,
          ...(activePanelsRef.current || []),
        ]);
      }
    }
  }, [data.collapsed]);

  return (
    <div className={cx("accordion-block", className)}>
      {data.headline && <h2 className="headline">{data.headline}</h2>}
      {data.filtering && (
        <AccordionFilter
          config={accordionConfig}
          data={data}
          filterValue={filterValue}
          handleFilteredValueChange={handleFilteredValueChange}
        />
      )}
      {panels
        .filter(
          (panel) =>
            !data.filtering ||
            filterValue === "" ||
            (filterValue !== "" &&
              panel[1].title
                ?.toLowerCase()
                .includes(filterValue.toLowerCase())),
        )
        .map(([id, panel], index) => {
          const active = isActive(id);
          return accordionBlockHasValue(panel) ? (
            <div
              key={id}
              id={id}
              className={cx("accordion-panel", { "is-active": active })}
            >
              <button
                className={cx("accordion-panel__title", {
                  "is-active": active,
                  "icon-right": iconOnRight,
                })}
                aria-expanded={active}
                onClick={() => handleClick(id, index)}
                onKeyDown={(e) => {
                  if (e.keyCode === 13 || e.keyCode === 32) {
                    e.preventDefault();
                    handleClick(id, index);
                  }
                }}
              >
                <span>{panel?.title}</span>
                <span className="accordion-panel__icon" aria-hidden="true">
                  {active ? "−" : "+"}
                </span>
              </button>
              <AnimateHeight
                animateOpacity
                duration={500}
                height={active || diffView ? "auto" : 0}
                onTransitionEnd={() => {
                  if (!!activePanels && id === itemToScroll) {
                    scrollToElement();
                    setItemToScroll("");
                  }
                }}
              >
                <div className="accordion-panel__content">
                  <RenderBlocks
                    {...props}
                    location={location}
                    metadata={metadata}
                    content={panel}
                  />
                </div>
              </AnimateHeight>
            </div>
          ) : null;
        })}
    </div>
  );
};

export default withBlockExtensions(View);
